import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from './apiClient';
import { DATA_MODE } from '@/config/dataMode';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
}

interface AuthContextShape {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextShape | null>(null);

/**
 * In demo mode (no backend deployed) this provider is a no-op: `user`
 * resolves to a synthetic local user and nothing ever redirects to
 * /login, so every existing demo page keeps working exactly as before
 * (brief: "do not remove existing... demo functionality"). Real
 * authentication only activates once DATA_MODE === 'live'.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(DATA_MODE === 'demo' ? { id: 'local', email: 'local@ugarits.demo', displayName: 'Maher (local)', roles: ['owner'] } : null);
  const [loading, setLoading] = useState(DATA_MODE === 'live');

  useEffect(() => {
    if (DATA_MODE !== 'live') return;
    api.get<AuthUser>('/api/auth/me')
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  async function login(email: string, password: string) {
    if (DATA_MODE !== 'live') return;
    try {
      const u = await api.post<{ id: string; email: string; displayName: string }>('/api/auth/login', { email, password });
      const me = await api.get<AuthUser>('/api/auth/me');
      setUser(me);
      void u;
    } catch (e) {
      if (e instanceof ApiError) throw new Error(e.status === 423 ? 'Account temporarily locked — too many failed attempts.' : 'Invalid email or password.');
      throw e;
    }
  }

  async function logout() {
    if (DATA_MODE !== 'live') return;
    await api.post('/api/auth/logout').catch(() => undefined);
    setUser(null);
  }

  return <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
